import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  MapPin,
  Home,
  Building2,
  Store,
  Key,
  Building,
  LayoutGrid,
  BedDouble,
  Hotel,
  Bed,
  Trees,
  Palmtree,
  Sparkles,
  CalendarDays,
  ShieldCheck,
  Compass,
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { CategoryItem } from '../types';
import { OptimizedImage } from './OptimizedImage';

interface BrowseCategoriesProps {
  onSelectCategory?: (category: CategoryItem) => void;
}

type TabType = 'all' | 'buy' | 'rent' | 'stay';

interface CategoryCardDef {
  id: string;
  tab: 'buy' | 'rent' | 'stay';
  nameEn: string;
  nameFr: string;
  descEn: string;
  descFr: string;
  routeEn: string;
  routeFr: string;
  badgeEn: string;
  badgeFr: string;
  image: string;
  icon: React.ReactNode;
}

const ALL_MARKET_CATEGORIES: CategoryCardDef[] = [
  // === BUY CATEGORIES ===
  {
    id: 'land',
    tab: 'buy',
    nameEn: 'Land & Plots for Sale',
    nameFr: 'Terrains & Parcelles à Vendre',
    descEn: 'Titled building plots, commercial acreage, agricultural farmland',
    descFr: 'Terrains titrés, parcelles constructibles, concessions agricoles',
    routeEn: '/en/land-for-sale',
    routeFr: '/fr/terrains-a-vendre',
    badgeEn: 'TITLED LAND',
    badgeFr: 'TERRAIN TITRÉ',
    image: '/images/categories/land.jpg',
    icon: <MapPin className="w-4 h-4 text-emerald-400" />,
  },
  {
    id: 'houses-sale',
    tab: 'buy',
    nameEn: 'Houses & Duplexes for Sale',
    nameFr: 'Maisons & Duplex à Vendre',
    descEn: 'Modern villas, private duplexes, residential compounds',
    descFr: 'Villas modernes, duplex standing, résidences sécurisées',
    routeEn: '/en/houses-for-sale',
    routeFr: '/fr/maisons-a-vendre',
    badgeEn: 'FOR SALE',
    badgeFr: 'À VENDRE',
    image: '/images/categories/houses-sale.jpg',
    icon: <Home className="w-4 h-4 text-emerald-400" />,
  },
  {
    id: 'apartments-sale',
    tab: 'buy',
    nameEn: 'Apartments for Sale',
    nameFr: 'Appartements à Vendre',
    descEn: 'Condominiums, penthouse apartments, buy-to-let units',
    descFr: 'Appartements neufs, copropriétés, investissements locatifs',
    routeEn: '/en/apartments-for-sale',
    routeFr: '/fr/appartements-a-vendre',
    badgeEn: 'FOR SALE',
    badgeFr: 'À VENDRE',
    image: '/images/categories/apartments-sale.jpg',
    icon: <Building2 className="w-4 h-4 text-emerald-400" />,
  },
  {
    id: 'commercial-sale',
    tab: 'buy',
    nameEn: 'Shops & Offices for Sale',
    nameFr: 'Boutiques & Bureaux à Vendre',
    descEn: 'Retail shops, commercial boutiques, office suites, business buildings',
    descFr: 'Boutiques, magasins, bureaux d’affaires, immeubles commerciaux',
    routeEn: '/en/commercial-for-sale',
    routeFr: '/fr/immobilier-commercial-a-vendre',
    badgeEn: 'SHOPS & OFFICES',
    badgeFr: 'BOUTIQUES & BUREAUX',
    image: '/images/categories/commercial-sale.jpg',
    icon: <Store className="w-4 h-4 text-emerald-400" />,
  },

  // === RENT CATEGORIES ===
  {
    id: 'houses-rent',
    tab: 'rent',
    nameEn: 'Houses & Villas for Rent',
    nameFr: 'Maisons & Villas à Louer',
    descEn: 'Family residences, executive villas with courtyards & gardens',
    descFr: 'Résidences familiales, villas de standing avec cour et jardin',
    routeEn: '/en/houses-for-rent',
    routeFr: '/fr/maisons-a-louer',
    badgeEn: 'FOR RENT',
    badgeFr: 'LOCATION',
    image: '/images/categories/houses-rent.jpg',
    icon: <Key className="w-4 h-4 text-amber-400" />,
  },
  {
    id: 'apartments-rent',
    tab: 'rent',
    nameEn: 'Apartments for Rent',
    nameFr: 'Appartements à Louer',
    descEn: '1 to 4 bedroom apartments in high-demand residential sectors',
    descFr: 'Appartements 1 à 4 chambres dans quartiers bien situés',
    routeEn: '/en/apartments-for-rent',
    routeFr: '/fr/appartements-a-louer',
    badgeEn: 'FOR RENT',
    badgeFr: 'LOCATION',
    image: '/images/categories/apartments-rent.jpg',
    icon: <Building className="w-4 h-4 text-amber-400" />,
  },
  {
    id: 'studios-rent',
    tab: 'rent',
    nameEn: 'Studios & Mini-Flats',
    nameFr: 'Studios Modernes',
    descEn: 'Modern self-contained studio flats for executives and students',
    descFr: 'Studios indépendants équipés pour jeunes cadres et étudiants',
    routeEn: '/en/studios-for-rent',
    routeFr: '/fr/studios-a-louer',
    badgeEn: 'STUDIOS',
    badgeFr: 'STUDIOS',
    image: '/images/categories/studios-rent.jpg',
    icon: <LayoutGrid className="w-4 h-4 text-amber-400" />,
  },
  {
    id: 'rooms-rent',
    tab: 'rent',
    nameEn: 'Single Rooms for Rent',
    nameFr: 'Chambres Simples',
    descEn: 'Budget-friendly private rooms in secure residences',
    descFr: 'Chambres individuelles abordables en résidences calmes',
    routeEn: '/en/rooms-for-rent',
    routeFr: '/fr/chambres-a-louer',
    badgeEn: 'ROOMS',
    badgeFr: 'CHAMBRES',
    image: '/images/categories/rooms-rent.jpg',
    icon: <BedDouble className="w-4 h-4 text-amber-400" />,
  },
  {
    id: 'commercial-rent',
    tab: 'rent',
    nameEn: 'Shops & Offices for Rent',
    nameFr: 'Boutiques & Bureaux à Louer',
    descEn: 'Retail shops, roadside boutiques, executive offices, commercial buildings',
    descFr: 'Boutiques en bordure de route, bureaux d’affaires, espaces commerciaux',
    routeEn: '/en/commercial-for-rent',
    routeFr: '/fr/locaux-commerciaux-a-louer',
    badgeEn: 'SHOPS & OFFICES',
    badgeFr: 'BOUTIQUES & BUREAUX',
    image: '/images/categories/commercial-rent.jpg',
    icon: <Store className="w-4 h-4 text-amber-400" />,
  },

  // === STAY CATEGORIES ===
  {
    id: 'short-stays',
    tab: 'stay',
    nameEn: 'Short-Stay & Furnished',
    nameFr: 'Locations Meublées',
    descEn: 'Fully furnished apartments with WiFi and backup electricity',
    descFr: 'Appartements meublés avec groupe électrogène et forage',
    routeEn: '/en/short-stays',
    routeFr: '/fr/courts-sejours',
    badgeEn: 'SHORT STAY',
    badgeFr: 'COURT SÉJOUR',
    image: '/images/categories/short-stays.jpg',
    icon: <CalendarDays className="w-4 h-4 text-cyan-400" />,
  },
  {
    id: 'hotels',
    tab: 'stay',
    nameEn: 'Hotels & Motels',
    nameFr: 'Hôtels & Auberges',
    descEn: 'Registered hotel rooms and hospitality suites across Cameroon',
    descFr: 'Chambres d’hôtels homologuées et suites dans tout le pays',
    routeEn: '/en/hotels',
    routeFr: '/fr/hotels',
    badgeEn: 'HOTELS',
    badgeFr: 'HÔTELLERIE',
    image: '/images/categories/hotels.jpg',
    icon: <Hotel className="w-4 h-4 text-cyan-400" />,
  },
  {
    id: 'guesthouses',
    tab: 'stay',
    nameEn: 'Guesthouses & Lodges',
    nameFr: 'Résidences & Maisons d’Hôtes',
    descEn: 'Charming private lodges, mountain retreats and eco-camps',
    descFr: 'Maisons d’hôtes chaleureuses, lodges écotouristiques',
    routeEn: '/en/guesthouses',
    routeFr: '/fr/maisons-dhotes',
    badgeEn: 'LODGES',
    badgeFr: 'LODGES',
    image: '/images/categories/guesthouses.jpg',
    icon: <Trees className="w-4 h-4 text-cyan-400" />,
  },
  {
    id: 'resorts',
    tab: 'stay',
    nameEn: 'Resorts & Holiday Homes',
    nameFr: 'Complexes & Villas Vacances',
    descEn: 'Beachfront resorts in Kribi & Limbe, luxury holiday compounds',
    descFr: 'Complexes balnéaires à Kribi et Limbe, villas de villégiature',
    routeEn: '/en/resorts',
    routeFr: '/fr/complexes-hoteliers',
    badgeEn: 'RESORTS',
    badgeFr: 'BALNÉAIRE',
    image: '/images/categories/resorts.jpg',
    icon: <Palmtree className="w-4 h-4 text-cyan-400" />,
  },
];

export const BrowseCategories: React.FC<BrowseCategoriesProps> = ({ onSelectCategory }) => {
  const { language } = useLanguage();
  const isFr = language === 'fr';
  const [activeTab, setActiveTab] = useState<TabType>('all');

  const filteredCategories =
    activeTab === 'all'
      ? ALL_MARKET_CATEGORIES
      : ALL_MARKET_CATEGORIES.filter((cat) => cat.tab === activeTab);

  const tabs: { id: TabType; labelEn: string; labelFr: string; count: number }[] = [
    { id: 'all', labelEn: 'All Categories', labelFr: 'Toutes les Catégories', count: ALL_MARKET_CATEGORIES.length },
    { id: 'buy', labelEn: 'Buy', labelFr: 'Acheter', count: ALL_MARKET_CATEGORIES.filter((c) => c.tab === 'buy').length },
    { id: 'rent', labelEn: 'Rent', labelFr: 'Louer', count: ALL_MARKET_CATEGORIES.filter((c) => c.tab === 'rent').length },
    { id: 'stay', labelEn: 'Short Stays & Hotels', labelFr: 'Séjours & Hôtels', count: ALL_MARKET_CATEGORIES.filter((c) => c.tab === 'stay').length },
  ];

  return (
    <section className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
      {/* Section Header */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-100 text-[#094426] text-xs font-bold uppercase tracking-wider mb-2">
            <span>{isFr ? 'Marché Immobilier Camerounais' : 'Cameroon Real Estate Directory'}</span>
          </div>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
            {isFr ? 'Explorer Toutes les Catégories' : 'Explore All Real Estate Categories'}
          </h2>
          <p className="text-sm text-slate-600 mt-1 max-w-xl">
            {isFr
              ? 'Terrains titrés, villas, appartements, bureaux et meublés classés par type de transaction sur les 10 régions.'
              : 'Titled land, houses, apartments, commercial space, and furnished stays arranged across all 10 regions.'}
          </p>
        </div>

        <Link
          to={isFr ? '/fr/biens-a-vendre' : '/en/properties-for-sale'}
          className="text-[#b45309] hover:text-[#92400e] text-xs sm:text-sm font-semibold flex items-center gap-1.5 transition-colors shrink-0 group self-start sm:self-auto"
        >
          <span>{isFr ? 'Voir toutes les annonces' : 'View all listings'}</span>
          <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
        </Link>
      </div>

      {/* Category Tabs (Buy / Rent / Stay / All) */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2 mb-6 scrollbar-none">
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all whitespace-nowrap cursor-pointer flex items-center gap-2 ${
                isActive
                  ? 'bg-[#094426] text-white shadow-sm'
                  : 'bg-white hover:bg-slate-100 text-slate-700 border border-slate-200'
              }`}
            >
              <span>{isFr ? tab.labelFr : tab.labelEn}</span>
              <span
                className={`text-[10px] px-1.5 py-0.2 rounded-full font-bold ${
                  isActive ? 'bg-amber-400 text-slate-950' : 'bg-slate-100 text-slate-600'
                }`}
              >
                {tab.count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Clean Category Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
        {filteredCategories.map((cat) => {
          const targetRoute = isFr ? cat.routeFr : cat.routeEn;
          const displayName = isFr ? cat.nameFr : cat.nameEn;
          const displayDesc = isFr ? cat.descFr : cat.descEn;
          const displayBadge = isFr ? cat.badgeFr : cat.badgeEn;

          return (
            <Link
              key={cat.id}
              to={targetRoute}
              onClick={() => {
                if (onSelectCategory) {
                  onSelectCategory({
                    id: `cat-${cat.id}`,
                    name: displayName,
                    subtitle: displayDesc,
                    icon: cat.id,
                    color: 'emerald',
                  });
                }
              }}
              className="group relative rounded-2xl overflow-hidden bg-slate-900 border border-slate-200/80 shadow-xs hover:shadow-md transition-all duration-300 cursor-pointer flex flex-col justify-end min-h-[168px] sm:min-h-[184px]"
            >
              {/* Responsive Lazy AVIF/WebP Background Image */}
              <OptimizedImage
                src={cat.image}
                alt={displayName}
                watermark={false}
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 opacity-85"
              />

              {/* Subtle Editorial Gradient Scrim */}
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/40 to-transparent" />

              {/* Content Overlay */}
              <div className="relative z-10 p-4 flex flex-col justify-end">
                {/* Top Badge */}
                <div className="flex items-center justify-between mb-1.5">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-white/15 backdrop-blur-md border border-white/20 text-[10px] font-semibold text-white tracking-wide">
                    {cat.icon}
                    <span>{displayBadge}</span>
                  </span>
                </div>

                {/* Category Title */}
                <h3 className="font-bold text-base sm:text-[17px] text-white group-hover:text-emerald-200 transition-colors line-clamp-1">
                  {displayName}
                </h3>

                {/* Subtitle / Description */}
                <p className="text-slate-200/90 text-xs mt-0.5 line-clamp-2 leading-relaxed">
                  {displayDesc}
                </p>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
};
